
import { keyBy, uniq } from 'lodash';
export default class LocalData {

  constructor($iDB, useIndexedDB) {
    this.$iDB = $iDB;
    this.useIndexedDB = useIndexedDB;
  }

  setUseIndexedDB(value) {
    this.useIndexedDB = !!value;
  }

  handleDBError(error) {
    console.error(error);
    // 处理异常
  }

  async getLocalData(operator) {
    if (!this.useIndexedDB) {
      return;
    }
    const iDB = await this.$iDB;
    if (!iDB){
      return;
    }
    try {
      return await iDB.asyncDBOperator(operator);
    } catch (error) {
      this.handleDBError(error);
    }
  }

  /**
   * 获取本地布局信息
   * @param {*} issueTypeId
   * @returns
   */
  async getLocalLayout(issueTypeId) {
    return await this.getLocalData(db => db.issueLayout.get(issueTypeId));
  }

  /**
   * 获取本地事项详情
   * @param {*} id
   * @returns
   */
  async getLocalIssue(id) {
    const data = await this.getLocalData(db => db.issue.get(id));
    if (data) {
      const assigneeId = data.assigneeVO?.id;
      if (assigneeId) {
        const member = await this.getLocalData(db => db.projectMember.get(assigneeId));
        if (member) {
          data.assigneeVO = {
            avatar: member.avatar,
            name: member.username,
            id: member.id
          };
        }
      }
    }
    return data;
  }

  /**
   * 获取本地事项类型
   * @param {*} projectId
   * @returns
   */
  async getLocalIssueTypes(projectId) {
    return await this.getLocalData(db => db.issueType.where({ projectId }).toArray());
  }

  /**
   * 获取本地子任务
   * @param {*} id
   * @returns
   */
  async getLocalSubtasks(id) {
    let data = await this.getLocalData(db => db.issue.where({ parentId: id }).toArray());
    if (data?.length) {
      data = await Promise.all(
        data.map(
          async item => {
            let assigneeVO = item.assigneeVO || {};
            const assigneeId = assigneeVO.id;
            if (assigneeId) {
              const member = await this.getLocalData(db => db.projectMember.get(assigneeId));
              if (member) {
                assigneeVO = {
                  avatar: member.avatar,
                  name: member.username,
                  id: member.id
                };
              }
            }
            return {
              ...item,
              assigneeVO
            };
          }
        )
      );
    }
    return data;
  }

  /**
 * 获取本地项目列表
 * @param {*} projectId
 * @returns
 */
  async getLocalProjects() {
    return await this.getLocalData(db => db.project.toArray());
  }


  /**
   * 获取本地工作流
   * @param {*} id
   * @returns
   */
  async getLocalWorkflow(id) {
    return await this.getLocalData(db => db.workflow.get(id));
  }

  /**
   * 获取本地关联关系
   * @param {*} id
   * @returns
   */
  async getLocalRelation(id) {
    if (!id) {
      return;
    }
    const relationData = await this.getLocalData(db => db.issueRelationship.get(id));
    const relations = relationData?.relations;
    if (!relations?.length) {
      return [];
    }
    const uniQRelationIds = uniq(relations.map(item => item.destinationIssueId));
    const issues = await this.getLocalData(db => db.issue.where('id').anyOf(uniQRelationIds).toArray());
    if (uniQRelationIds.length !== issues.length) {
      return null;
    }
    const issueMap = keyBy(issues, 'id');
    const projectsPromises = relations.map(item => issueMap[item.destinationIssueId]).map(issue => this.getLocalData(db => db.project.get(issue.projectId)));
    const projects = await Promise.all(projectsPromises);
    const projectMap = keyBy(projects, 'id');
    for (let i = 0; i < relations.length; i++) {
      const item = relations[i];
      const issue = issueMap[item.destinationIssueId];
      const project = projectMap[issue.projectId] || {};
      relations[i] = {
        project: { ...project, name: project.pname, hasPermission: true },
        type: item.linkType,
        id: item.id,
        issueId: item.destinationIssueId,
        issueType: issue.issueTypeIdVO,
        issueNumber: issue.issueNumber,
        priority: issue.priority,
        statusVO: issue.statusVO,
        status: issue.statusVO,
        summary: issue.summary
      };
    }
    return relations;
  }

  /**
   * 获取迭代列表
   * @param {*} projectId
   * @returns
   */
  async getLocalSprints(projectId) {
    return await this.getLocalData(db => db.sprint.where({ projectId }).reverse().sortBy('updateTime'));
  }

  /**
   * 获取版本列表
   * @returns
   */
  async getLocalReleaseVersions() {
    return await this.getLocalData(db => db.releaseVersion.toCollection().reverse().sortBy('updateTime'));
  }

  /**
   * 获取标签列表
   * @param {*} projectId
   * @returns
   */
  async getLocalLabels(projectId) {
    return await this.getLocalData(db => db.label.where({ projectId }).reverse().sortBy('updateTime'));
  }

  /**
   * 获取优先级列表
   *
   * @returns
  */
  async getLocalPriorities() {
    return await this.getLocalData(db => db.priority.toCollection().reverse().sortBy('updateTime'));
  }

  /**
   * 获取史诗列表
   * @param {*} projectId
   * @returns
   */
  async getLocalEpics(projectId) {
    return await this.getLocalData(db => db.issue.where({ 'issueTypeIdVO.code': 'epic', projectId }).reverse().sortBy('updateTime'));
  }

  async getSystemFieldOptions(fieldCode, projectId) {
    const OPTION_MAP = {
      sprint: 'getLocalSprints',
      label: 'getLocalLabels',
      epic: 'getLocalEpics',
      issueTypeId: 'getLocalIssueTypes',
      releaseVersion: 'getLocalReleaseVersions',
      priority: 'getLocalPriorities'
    };
    if (this[OPTION_MAP[fieldCode]]) {
      return await this[OPTION_MAP[fieldCode]](projectId);
    }
    return null;
  }
}


